import React, { useEffect, useMemo, useState } from 'react';
import { pr as api } from '../services/api';
import FiltresPanel from './FiltresPanel';
import { useFiltresVentes, usePanneau, basculer } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';
import './Stock.css';
import './PrStock.css';

// ─── PR — Stock (2026-10-29) ────────────────────────────────────────────────
// Stock de pièces de rechange : photo du jour de l'export Datacar et fins de
// mois depuis décembre 2025 (A97 DATA, marts.stock_pr / stock_pr_mensuel).
// Valorisation au prix d'achat (PA) ou au PUMP borné, au choix (URL `val`).
// Ancienneté = MOIS SANS MOUVEMENT (quantité inchangée aux fins de mois), pas
// l'« âge » de l'export, qui court depuis la création de la référence.
// Blocs : tuiles, par concession (mailles), répartition
// par marque / famille / dépôt (un clic filtre les références), références.
// L'évolution fin de mois a son onglet : PR — Évolution du stock (2026-10-29).

const nf = new Intl.NumberFormat('fr-FR');
const cM = (v) => {
  v = Math.round(v || 0); const a = Math.abs(v);
  if (a >= 1e6) return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v / 1e6) + ' M€';
  if (a >= 1e3) return nf.format(Math.round(v / 1e3)) + ' k€';
  return nf.format(v) + ' €';
};
const k = (v) => (v == null ? '—' : nf.format(Math.round(v / 1000)));
const pc = (a, b) => (b ? `${Math.round((a / b) * 100)} %` : '—');
const eur2 = (v) => (v == null ? '—' : new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' €');
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const moisCourt = (ym) => `${MOIS[Number(ym.slice(5)) - 1]} ${ym.slice(2, 4)}`;
const moisLong = (ym) => `${['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'][Number(ym.slice(5)) - 1]} ${ym.slice(0, 4)}`;
const COUL = ['#0F766E', '#3E8F5F', '#DE9A2E', '#C0472F'];   // < 1 mois → 6 mois et +
const AXES = [{ c: 'fournisseur', l: 'Marque' }, { c: 'famille', l: 'Famille' }, { c: 'depot', l: 'Dépôt' }];
const COLS_REF = [
  { c: 'societe', l: 'Société' }, { c: 'depot', l: 'Dépôt' }, { c: 'reference', l: 'Référence' }, { c: 'designation', l: 'Désignation' },
  { c: 'fournisseur', l: 'Marque' }, { c: 'qte', l: 'Qté', num: true }, { c: 'dormant', l: 'Sans mouvement', num: true },
  { c: 'unitaire', l: 'Prix unitaire', num: true }, { c: 'valeur', l: 'Valeur', num: true },
];
const sansMvt = (r) => (r.sansMouvement == null ? '—' : r.sansMouvement === 0 ? '< 1 mois'
  : r.sansMouvement >= r.historique ? `≥ ${r.historique} mois` : `${r.sansMouvement} mois`);

export default function PrStock({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [refs, setRefs] = useState(null);
  const [erreur, setErreur] = useState('');
  const [tout, setTout] = useState(false);
  const [toutesRefs, setToutesRefs] = useState(false);
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const val = f.params.get('val') === 'pump' ? 'pump' : 'pa';
  const axe = AXES.some((a) => a.c === f.params.get('axe')) ? f.params.get('axe') : 'fournisseur';
  // Tri des références par colonne, côté serveur (sur toutes les lignes) : URL tri + sens.
  const tri = COLS_REF.some((c) => c.c === f.params.get('tri')) ? f.params.get('tri') : 'valeur';
  const sens = f.params.get('sens') === 'asc' ? 'asc' : (f.params.get('sens') === 'desc' ? 'desc' : (COLS_REF.find((c) => c.c === tri).num ? 'desc' : 'asc'));
  const trier = (c) => {
    const col = COLS_REF.find((x) => x.c === c); const defaut = col.num ? 'desc' : 'asc';
    const nouveau = c === tri ? (sens === 'desc' ? 'asc' : 'desc') : defaut;
    f.setFiltre({ tri: c === 'valeur' ? '' : c, sens: nouveau === defaut ? '' : nouveau });
  };
  const cle = f.params.get('cle') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.stock({ branche, societes: f.societesUrl || undefined })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, f.societesUrl]);
  useEffect(() => {
    let actif = true; setRefs(null);
    api.stockReferences({ branche, societes: f.societesUrl || undefined, tri: tri === 'valeur' ? undefined : tri, sens: f.params.get('sens') || undefined,
      axe: cle ? axe : undefined, cle: cle || undefined, val: val === 'pump' ? 'pump' : undefined })
      .then((r) => { if (actif) setRefs(r.data); }).catch(() => { if (actif) setRefs({ lignes: [], total: 0 }); });
    return () => { actif = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branche, f.societesUrl, tri, sens, axe, cle, val]);

  const feuilles = useMemo(() => (data?.parSociete || []).map((s) => ({ ...s, libelle: s.nom })), [data]);
  const axesDispo = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const sommer = (membres) => {
    const nbm = (data?.mois || []).length; const v0 = () => ({ pa: 0, pump: 0 });
    const t = { valeur: v0(), qte: 0, lignes: 0, refs: 0, tranches: [0, 1, 2, 3].map(v0), evolution: Array.from({ length: nbm }, () => null) };
    membres.forEach((m) => {
      ['pa', 'pump'].forEach((x) => { t.valeur[x] += m.valeur[x]; m.tranches.forEach((tr, i) => { t.tranches[i][x] += tr[x]; }); });
      t.qte += m.qte; t.lignes += m.lignes; t.refs += m.refs;
      m.evolution.forEach((e, i) => { if (e) { const c = t.evolution[i] || v0(); c.pa += e.pa; c.pump += e.pump; t.evolution[i] = c; } });
    });
    return t;
  };
  const maille = useRegroupement();
  const arbre = useMemo(() => construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => x.valeur?.pa),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = (
    <FiltresPanel ouvert={ouvert} onToggle={toggle}
      societesDispo={societesDispo} societesActives={f.selected} onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })} onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      couleurDe={f.couleurDe} nbActifs={(f.societesUrl ? 1 : 0) + (cle ? 1 : 0)} onReset={() => f.setFiltre({ societes: '', cle: '' })} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;
  if (data.indisponible || data.vide) {
    return <><main className="vt-page"><div className="vt-head"><div><h1>PR — Stock</h1></div></div>
      <div className="bi-empty"><h2>{data.indisponible ? 'Stock pièces non importé' : 'Aucune société sélectionnée'}</h2>
        <p>{data.indisponible ? 'Les exports stock pièces ne sont pas encore ingérés dans A97 DATA.' : 'Cochez au moins une société dans les filtres à droite.'}</p></div></main>{Panneau}</>;
  }

  const K = data.kpis; const V = (o) => (o ? o[val] : null);
  const valLib = val === 'pump' ? 'au PUMP borné' : 'au prix d’achat';
  const fm = K.finMois?.ym; const dFm = fm && V(K.finMois) ? (V(K.valeur) - V(K.finMois)) / V(K.finMois) : null;
  const lignesAxe = data.parAxe[axe] || []; const totAxe = lignesAxe.reduce((a, l) => a + l.valeur[val], 0);
  const AX = AXES.find((a) => a.c === axe);
  const dateStock = data.dateStock ? new Date(data.dateStock).toLocaleDateString('fr-FR') : '';
  const ligneSoc = (s, cellules) => {
    const cible = s.feuille ? s.code : (s.membres || []).join(',');
    return (
      <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
        onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
        <td className="l">
          <span className="rg-in" style={{ width: s.niveau * 14 }} />
          {s.feuille ? <span className="rg-chev vide" /> : (
            <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`} aria-label="Déplier"
              title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'}
              onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
          )}
          {s.libelle || s.nom}{!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
        </td>
        {cellules}
      </tr>
    );
  };

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>PR — Stock</h1></div>
          <div className="ps-tete">
            <span className="st-seg" aria-label="Valorisation" title="Valorisation de toute la page">
              <button type="button" className={val === 'pa' ? 'on' : ''} onClick={() => f.setFiltre({ val: '' })}>Prix d’achat</button>
              <button type="button" className={val === 'pump' ? 'on' : ''} onClick={() => f.setFiltre({ val: 'pump' })}>PUMP borné</button>
            </span>
            <div className="vt-asof"><b>stock au {dateStock}</b><br /><span>valorisé {valLib}</span></div>
          </div>
        </div>

        <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          <div className="vt-kpi"><div className="l">Valeur du stock</div><div className="v">{cM(V(K.valeur))}</div>
            <div className="d"><small>{valLib}</small></div></div>
          <div className="vt-kpi"><div className="l">Références</div><div className="v">{nf.format(K.refs)}</div>
            <div className="d"><small>{nf.format(Math.round(K.qte))} pièces en stock</small></div></div>
          <div className="vt-kpi"><div className="l">Sans mouvement depuis {data.dormant} mois et +</div><div className="v">{cM(V(K.dormant))}</div>
            <div className="d neg">{pc(V(K.dormant), V(K.valeur))}<small> du stock</small></div></div>
          <div className="vt-kpi"><div className="l">Évolution depuis fin {fm ? moisLong(fm) : '—'}</div>
            <div className="v">{dFm == null ? '—' : `${dFm > 0 ? '+' : ''}${(dFm * 100).toFixed(1).replace('.', ',')} %`}</div>
            <div className="d"><small>{fm ? `${cM(V(K.finMois))} fin ${moisCourt(fm)}` : 'aucune fin de mois importée'}</small></div></div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Par concession</h2>
            <div className="st-tools">
              <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axesDispo} />
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense ps-conc">
              <thead><tr>
                <th className="l">Société</th><th>Valeur</th><th>Réf.</th>
                {data.tranches.map((t, i) => <th key={t}><span style={{ color: COUL[i] }}>●</span> {t}</th>)}
                <th title={`Part du stock sans mouvement depuis ${data.dormant} mois et plus`}>Dormant</th><th className="l">Répartition</th>
              </tr></thead>
              <tbody>
                {arbre.map((s) => ligneSoc(s, <>
                  <td><b>{cM(V(s.valeur))}</b></td><td>{nf.format(s.refs)}</td>
                  {s.tranches.map((t, i) => <td key={i}>{k(V(t))}</td>)}
                  <td className={V(s.tranches[3]) / V(s.valeur) >= 0.5 ? 'ps-dormant' : ''}>{pc(V(s.tranches[3]), V(s.valeur))}</td>
                  <td className="l"><div className="ps-barre">{s.tranches.map((t, i) => <span key={i} style={{ width: `${V(s.valeur) ? (V(t) / V(s.valeur)) * 100 : 0}%`, background: COUL[i] }} />)}</div></td>
                </>))}
              </tbody>
            </table>
            <p className="hint">Sans mouvement = quantité inchangée aux fins de mois ; un clic sur une ligne filtre la page. Montants des tranches en k€. Réf. = références distinctes de la société (une référence présente dans deux sociétés compte dans chacune).</p>
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Répartition par {AX.l.toLowerCase()}</h2>
            <div className="st-tools">
              <span className="hint">un clic filtre les références</span>
              <span className="st-seg" aria-label="Axe">
                {AXES.map((a) => <button key={a.c} type="button" className={axe === a.c ? 'on' : ''} onClick={() => { setTout(false); f.setFiltre({ axe: a.c === 'fournisseur' ? '' : a.c, cle: '' }); }}>{a.l}</button>)}
              </span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th className="l">{AX.l}</th><th>Valeur</th><th>Part</th><th>Réf.</th><th>Pièces</th><th>Sans mvt {data.dormant} mois +</th><th>%</th></tr></thead>
              <tbody>
                {(tout ? lignesAxe : lignesAxe.slice(0, 25)).map((l) => (
                  <tr key={l.cle} className={`tbl-clic${cle === l.cle ? ' actif' : ''}`} onClick={() => f.setFiltre({ cle: basculer(cle, l.cle) })}>
                    <td className="l">{l.cle}</td><td><b>{cM(l.valeur[val])}</b></td><td>{pc(l.valeur[val], totAxe)}</td>
                    <td>{nf.format(l.refs)}</td><td>{nf.format(Math.round(l.qte))}</td><td>{cM(l.dormant[val])}</td>
                    <td className={l.valeur[val] && l.dormant[val] / l.valeur[val] >= 0.5 ? 'ps-dormant' : ''}>{pc(l.dormant[val], l.valeur[val])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {lignesAxe.length > 25 && (
              <button type="button" className="pl-lien ps-plus" onClick={() => setTout(!tout)}>{tout ? 'Afficher les 25 premières' : `Afficher les ${lignesAxe.length} lignes`}</button>
            )}
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Références</h2>
            <div className="st-tools">
              {cle && <span className="ps-filtre">{AX.l} : <b>{cle}</b><button type="button" title="Retirer le filtre" onClick={() => f.setFiltre({ cle: '' })}>×</button></span>}
              <span className="hint">un clic sur un en-tête trie toutes les lignes</span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            {!refs ? <div className="vt-skel" /> : (<>
              <p className="hint">{refs.total > refs.lignes.length ? `Les ${refs.lignes.length} premières lignes sur ${nf.format(refs.total)}` : `${nf.format(refs.total)} lignes`} (référence × dépôt), triées par {COLS_REF.find((c) => c.c === tri).l.toLowerCase()} {sens === 'desc' ? 'décroissant' : 'croissant'}.</p>
              <table className="vt-tbl tbl-dense">
                <thead><tr>{COLS_REF.map((c) => (
                  <th key={c.c} className={`${c.num ? '' : 'l'} st-triable${tri === c.c ? ' st-tri-on' : ''}`} onClick={() => trier(c.c)}
                    title="Trier sur cette colonne (toutes les lignes)">
                    {c.c === 'unitaire' ? `${val === 'pump' ? 'PUMP borné' : 'PA'} unit.` : c.l}{tri === c.c && <span className="st-tri-fleche">{sens === 'desc' ? '▾' : '▴'}</span>}
                  </th>
                ))}</tr></thead>
                <tbody>
                  {(toutesRefs ? refs.lignes : refs.lignes.slice(0, 50)).map((r, i) => (
                    <tr key={i}>
                      <td className="l">{r.societe}</td><td className="l">{r.depot}</td><td className="l ps-ref">{r.reference}</td>
                      <td className="l ps-des" title={r.designation}>{r.designation}</td><td className="l">{r.fournisseur}</td>
                      <td>{nf.format(r.qte)}</td><td className={r.sansMouvement >= data.dormant ? 'ps-dormant' : ''}>{sansMvt(r)}</td>
                      <td>{eur2(val === 'pump' ? r.pump : r.pa)}</td><td><b>{cM(r.valeur[val])}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {refs.lignes.length > 50 && (
                <button type="button" className="pl-lien ps-plus" onClick={() => setToutesRefs(!toutesRefs)}>{toutesRefs ? 'Afficher les 50 premières' : `Afficher les ${refs.lignes.length} lignes`}</button>
              )}
            </>)}
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
